import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { CalendarStatusType } from "src/helpers/Enums";
import { useLocation } from "react-router";
import SpecificCalendarHeader from "./SpecificCalendarHeader";
import apiService from "src/services/api.service";
import Revenue from "./Revenue";
import NewSchedule from "./NewSchedule";
import DeleteSchedule from "./DeleteSchedule";
import CancelSchedule from "./CancelSchedule";
import SchedulePopup from "./SchedulePopup";
import CreateLink from "./CreateLink";

const TOOLTIP_ARROW_GAP = 12;
const TOOLTIP_WIDTH = 415;

const SpecificCalendar = () => {
  const location = useLocation();
  const [creativeTooltip, setCreativeTooltip] = useState(null);
  const [copiedCreativeField, setCopiedCreativeField] = useState(null);
  const creativeTooltipCloseTimeoutRef = useRef(null);
  const [enterRevenue, setEnterRevenue] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [date, setDate] = useState(new Date(location.state?.date));
  const today = new Date();
  const [days, setDays] = useState(new Map());
  const [weekDays, setWeekDays] = useState([]);
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [chosenCalendar, setChosenCalendar] = useState();
  const [day, setDay] = useState();
  const [openNewSchedule, setOpenNewSchedule] = useState(false);
  const [dateForNewSchedule, setDateForNewSchedule] = useState();
  const [chosenList, setChosenList] = useState(location.state?.item?.list_id);
  const [openDelete, setOpenDelete] = useState(false);
  const [openCancelSchedule, setOpenCancelSchedule] = useState(false);
  const [chosenCalendarForCancel, setChosenCalendarForCancel] = useState();
  const [openSchedulePopup, setOpenSchedulePopup] = useState(false);
  const [schedulePopupData, setSchedulePopupData] = useState(null);
  const [openCreateLink, setOpenCreateLink] = useState(false);
  const [createLinkData, setCreateLinkData] = useState(null);
  const [idTooltipValue, setIdTooltipValue] = useState(null);
  const [idTooltipAnchorRect, setIdTooltipAnchorRect] = useState(null);
  const [idCopied, setIdCopied] = useState(false);
  const idTooltipRef = useRef(null);

  const handleCopyId = () => {
    const id = idTooltipValue?._id;
    if (id) {
      navigator.clipboard.writeText(id);
      setIdCopied(true);
    }
  };

  const handleCopyCreativeField = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopiedCreativeField(field);
    } catch (error) {
      setCopiedCreativeField(null);
    }
  };

  useEffect(() => {
    if (!idTooltipValue) return;
    const handleClickOutside = (e) => {
      if (
        idTooltipRef.current?.contains(e.target) ||
        e.target.closest("[data-id-tooltip-trigger]")
      )
        return;
      setIdTooltipValue(null);
      setIdTooltipAnchorRect(null);
      setIdCopied(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [idTooltipValue]);

  useEffect(() => {
    if (isUpdate) {
      apiService.getSpecificCalendar(date).then((response) => {
        setList(response.data);
        setFilteredList(
          response.data?.filter((item) => item.list_id?._id === chosenList?._id)
        );
      });
      setIsUpdate(false);
    }
  }, [date, isUpdate]);

  useEffect(() => {
    setFilteredList(
      list?.filter((item) => item.list_id?._id === chosenList?._id)
    );
  }, [chosenList]);

  useEffect(() => {
    setWeekDays(getWeekDays());
  }, [date]);

  useEffect(() => {
    const groups = new Map();
    filteredList?.forEach((item) => {
      const dayKey = item?.date
        ? parseInt(item?.date.substring(8, 10), 10)
        : null;

      if (!groups.has(dayKey)) {
        groups.set(dayKey, []);
      }
      groups.get(dayKey).push(item);
    });
    setDays(groups);
  }, [filteredList]);

  const getWeekDays = () => {
    const weekDates = [];
    const currentDay = date.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() + mondayOffset);

    for (let i = 0; i < 7; i++) {
      const weekDay = new Date(startOfWeek);
      weekDay.setDate(startOfWeek.getDate() + i);
      weekDates.push(weekDay?.getDate());
    }
    return weekDates;
  };

  const getItemsByKey = (key) => {
    if (days.has(key)) {
      return days.get(key);
    } else {
      return null;
    }
  };

  const getTotalRevenueForDay = (day) => {
    const items = getItemsByKey(day);
    if (!items) return 0;
    return Array.from(items.values()).reduce((total, item) => {
      return total + (parseFloat(item?.revenue) || 0);
    }, 0);
  };

  const getDayContent = (day) => {
    if (getItemsByKey(day) !== null) {
      return (
        <div className={"flex flex-col gap-[6px]"}>
          {Array.from(getItemsByKey(day).entries()).map(([key, value]) =>
            value.status === CalendarStatusType.DONE ? (
              <div className={"SpecificCalendarCompleteBox"}>
                <div className="flex items-center justify-between self-stretch w-full">
                  <div className={"CalendarCompleteBoxText"}>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/completedIcon.svg"}
                      className={"w-[10px] h-[10px]"}
                    />
                    Completed
                  </div>
                  <button
                    type="button"
                    data-id-tooltip-trigger
                    className={
                      idTooltipValue?._id === value?._id
                        ? "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-white"
                        : "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-[#58AD2F]"
                    }
                    onClick={(e) => {
                      if (idTooltipValue?._id === value?._id) {
                        setIdTooltipValue(null);
                        setIdTooltipAnchorRect(null);
                        setIdCopied(false);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setIdTooltipAnchorRect({
                          left: rect.left,
                          top: rect.top,
                          width: rect.width,
                          height: rect.height,
                        });
                        setIdTooltipValue(value);
                        setIdCopied(false);
                      }
                    }}
                  >
                    <img
                      alt={"Logo"}
                      src={
                        idTooltipValue?._id === value?._id
                          ? "/media/icons/code-02-gray.svg"
                          : "/media/icons/code-02-white.svg"
                      }
                      className={"w-[10px] h-[10px]"}
                    />
                  </button>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div
                  className={"flex justify-between items-center self-stretch"}
                >
                  <div className={"flex flex-wrap gap-1 items-center"}>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/currency-dollar-circle.svg"}
                      className={"w-[14px] h-[14px]"}
                    />
                    <div className={"DashboardListNameTitle"}>Revenue:</div>
                  </div>
                  <div className={"SpecificCalendarValues"}>
                    ${value?.revenue}
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div
                  className={"flex justify-between items-center self-stretch"}
                >
                  <div className={"flex flex-wrap gap-1 items-center"}>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/offer.svg"}
                      className={"w-[14px] h-[14px]"}
                    />
                    <div className={"DashboardListNameTitle"}>Offer Score:</div>
                  </div>
                  <div className={"SpecificCalendarValues"}>
                    {value?.score?.toFixed(2)}
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"flex flex-wrap gap-1 items-center"}>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/offer.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                  <div className={"DashboardListNameTitle"}>Offer Name:</div>
                  <div className={"SMSEmailBoxListName"}>
                    {value?.offer_id?.name}
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <button className="" onClick={() => handleRevenue(value, day)}>
                  <div className={"flex justify-start"}>
                    <div className={"text-left"}>{value.offer_id?.name}</div>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/arrowRight.svg"}
                      className={"w-4 h-4"}
                    />
                  </div>
                </button>
                <button
                  className={"SpecificCalendarCompletedDelete"}
                  onClick={() => handleCancelSchedule(value)}
                >
                  <img
                    alt={"Logo"}
                    src={"/media/icons/trash-03.svg"}
                    className={"w-4 h-4"}
                  />
                  Delete
                </button>
              </div>
            ) : value.status === CalendarStatusType.SCHEDULED ? (
              <div className="SpecificCalendarScheduledBox">
                <div className="flex items-center justify-between self-stretch w-full">
                  <div className={"CalendarScheduledBoxText"}>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/completedIcon.svg"}
                      className={"w-[10px] h-[10px]"}
                    />
                    Scheduled
                  </div>
                  <button
                    type="button"
                    data-id-tooltip-trigger
                    className={
                      idTooltipValue?._id === value?._id
                        ? "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-white"
                        : "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-[#DD2E2E]"
                    }
                    onClick={(e) => {
                      if (idTooltipValue?._id === value?._id) {
                        setIdTooltipValue(null);
                        setIdTooltipAnchorRect(null);
                        setIdCopied(false);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setIdTooltipAnchorRect({
                          left: rect.left,
                          top: rect.top,
                          width: rect.width,
                          height: rect.height,
                        });
                        setIdTooltipValue(value);
                        setIdCopied(false);
                      }
                    }}
                  >
                    <img
                      alt={"Logo"}
                      src={
                        idTooltipValue?._id === value?._id
                          ? "/media/icons/code-02-gray.svg"
                          : "/media/icons/code-02-white.svg"
                      }
                      className={"w-[10px] h-[10px]"}
                    />
                  </button>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"flex flex-wrap gap-1 items-center"}>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/offer.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                  <div className={"DashboardListNameTitle"}>Offer Name:</div>
                  <div className={"SMSEmailBoxListName"}>
                    {value?.offer_id?.name}
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"flex flex-wrap gap-1 items-center"}>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/stars-01.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                  <div className={"DashboardListNameTitle"}>Creative Name:</div>
                  <span
                    className={`SMSEmailBoxListName inline-block select-none transition-colors ${
                      creativeTooltip?.value?._id === value?._id
                        ? "CreativeNameLinkHover"
                        : ""
                    }`}
                    onMouseEnter={(e) => {
                      clearTimeout(creativeTooltipCloseTimeoutRef.current);
                      const r = e.currentTarget.getBoundingClientRect();
                      setCreativeTooltip({
                        rect: {
                          left: r.left,
                          top: r.top,
                          width: r.width,
                          height: r.height,
                        },
                        value,
                      });
                    }}
                    onMouseLeave={() => {
                      creativeTooltipCloseTimeoutRef.current = setTimeout(
                        () => setCreativeTooltip(null),
                        150
                      );
                    }}
                    role="text"
                  >
                    {value?.creative_id?.name}
                  </span>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div
                  className={"flex flex-col flex-wrap gap-[2px] items-start"}
                >
                  <div className="flex gap-1 items-center">
                    <img
                      alt={"Logo"}
                      src={"/media/icons/git-branch-01.svg"}
                      className={"w-[14px] h-[14px]"}
                    />
                    <div className={"DashboardListNameTitle"}>Segment:</div>
                  </div>
                  <div className="flex gap-1 items-center justify-start">
                    <div
                      className={
                        value?.list_id?.segment === "Live"
                          ? "ListSegmentLive"
                          : "ListSegmentRepair"
                      }
                    ></div>
                    <div className={"SMSEmailBoxListName"}>
                      {value?.list_id?.segment}
                    </div>
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"text-left"}>
                  Revenue input is required for this list.
                </div>
                {today >= date && (
                  <button
                    className={"SpecificCalendarRevenue"}
                    onClick={() => handleRevenue(value, day)}
                  >
                    <img
                      alt={"Logo"}
                      src={"/media/icons/arrowRightRed.svg"}
                      className={"w-4 h-4"}
                    />
                    Enter Revenue
                  </button>
                )}
                {today >= date && (
                  <button
                    className={"SpecificCalendarScheduledDelete"}
                    onClick={() => handleCancelSchedule(value)}
                  >
                    <img
                      alt={"Logo"}
                      src={"/media/icons/deleteIcon.svg"}
                      className={"w-4 h-4"}
                    />
                    Delete
                  </button>
                )}
              </div>
            ) : (
              <div className="SpecificCalendarCreatedBox">
                <div className="flex items-center justify-between self-stretch w-full">
                  <div className={"CalendarCreatedBoxText"}>
                    <img
                      alt={"Logo"}
                      src={"/media/icons/createdIcon.svg"}
                      className={"w-[10px] h-[10px]"}
                    />
                    Created
                  </div>
                  <button
                    type="button"
                    data-id-tooltip-trigger
                    className={
                      idTooltipValue?._id === value?._id
                        ? "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-white"
                        : "flex h-[18px] px-[6px] items-center gap-1 rounded-[34px] bg-[#7E50EA]"
                    }
                    onClick={(e) => {
                      if (idTooltipValue?._id === value?._id) {
                        setIdTooltipValue(null);
                        setIdTooltipAnchorRect(null);
                        setIdCopied(false);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setIdTooltipAnchorRect({
                          left: rect.left,
                          top: rect.top,
                          width: rect.width,
                          height: rect.height,
                        });
                        setIdTooltipValue(value);
                        setIdCopied(false);
                      }
                    }}
                  >
                    <img
                      alt={"Logo"}
                      src={
                        idTooltipValue?._id === value?._id
                          ? "/media/icons/code-02-gray.svg"
                          : "/media/icons/code-02-white.svg"
                      }
                      className={"w-[10px] h-[10px]"}
                    />
                  </button>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"flex flex-wrap gap-1 items-center"}>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/offer.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                  <div className={"DashboardListNameTitle"}>Offer Name:</div>
                  <div className={"SMSEmailBoxListName"}>
                    {value?.offer_id?.name}
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"flex flex-wrap gap-1 items-center"}>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/stars-01.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                  <div className={"DashboardListNameTitle"}>Creative Name:</div>
                  <span
                    className={`SMSEmailBoxListName inline-block select-none transition-colors ${
                      creativeTooltip?.value?._id === value?._id
                        ? "CreativeNameLinkHover"
                        : ""
                    }`}
                    onMouseEnter={(e) => {
                      clearTimeout(creativeTooltipCloseTimeoutRef.current);
                      const r = e.currentTarget.getBoundingClientRect();
                      setCreativeTooltip({
                        rect: {
                          left: r.left,
                          top: r.top,
                          width: r.width,
                          height: r.height,
                        },
                        value,
                      });
                    }}
                    onMouseLeave={() => {
                      creativeTooltipCloseTimeoutRef.current = setTimeout(
                        () => setCreativeTooltip(null),
                        150
                      );
                    }}
                    role="text"
                  >
                    {value?.creative_id?.name}
                  </span>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div
                  className={"flex flex-col flex-wrap gap-[2px] items-start"}
                >
                  <div className="flex gap-1 items-center">
                    <img
                      alt={"Logo"}
                      src={"/media/icons/git-branch-01.svg"}
                      className={"w-[14px] h-[14px]"}
                    />
                    <div className={"DashboardListNameTitle"}>Segment:</div>
                  </div>
                  <div className="flex gap-1 items-center justify-start">
                    <div
                      className={
                        value?.list_id?.segment === "Live"
                          ? "ListSegmentLive"
                          : "ListSegmentRepair"
                      }
                    ></div>
                    <div className={"SMSEmailBoxListName"}>
                      {value?.list_id?.segment}
                    </div>
                  </div>
                </div>
                <div className={"HorizontalDividerDarker"}></div>
                <div className={"text-left"}>The list has been scheduled.</div>
                {today >= date && (
                  <button
                    className={"SpecificCalendarCreatedSchedule"}
                    onClick={() => handleSchedulePopup(value, day)}
                  >
                    <img
                      alt={"Logo"}
                      src={"/media/icons/clockPurple.svg"}
                      className={"w-4 h-4"}
                    />
                    Schedule
                  </button>
                )}
                {today >= date && (
                  <button
                    className={"SpecificCalendarCreatedDelete"}
                    onClick={() => handleDeleteSchedule(value)}
                  >
                    <img
                      alt={"Logo"}
                      src={"/media/icons/deleteIconPurple.svg"}
                      className={"w-4 h-4"}
                    />
                    Delete
                  </button>
                )}
              </div>
            )
          )}
          {!isPastDay(day) && (
            <button
              className={"SpecificCalendarAddButton"}
              onClick={() => handleAddSchedule(day)}
            >
              +
            </button>
          )}
        </div>
      );
    } else {
      if (isPastDay(day)) {
        return <div></div>;
      }
      return (
        <div className="CalendarDayBox">
          <button
            className={"SpecificCalendarAddButton"}
            onClick={() => handleAddSchedule(day)}
          >
            +
          </button>
        </div>
      );
    }
  };

  const handlePreviousMonth = () => {
    const currentDay = date.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const startOfCurrentWeek = new Date(date);
    startOfCurrentWeek.setDate(date.getDate() + mondayOffset);

    const startOfPreviousWeek = new Date(startOfCurrentWeek);
    startOfPreviousWeek.setDate(startOfCurrentWeek.getDate() - 7);
    setDate(startOfPreviousWeek);
    setIsUpdate(true);
  };

  const handleNextMonth = () => {
    const currentDay = date.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const startOfCurrentWeek = new Date(date);
    startOfCurrentWeek.setDate(date.getDate() + mondayOffset);

    const startOfPreviousWeek = new Date(startOfCurrentWeek);
    startOfPreviousWeek.setDate(startOfCurrentWeek.getDate() + 7);
    setDate(startOfPreviousWeek);
    setIsUpdate(true);
  };

  const isSameDay = (day) => {
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dayIndex = dayNames.indexOf(day);
    if (dayIndex === -1) return false;

    const currentDay = date.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() + mondayOffset);

    const targetDate = new Date(startOfWeek);
    targetDate.setDate(startOfWeek.getDate() + dayIndex);

    return (
      targetDate.getFullYear() === today.getFullYear() &&
      targetDate.getMonth() === today.getMonth() &&
      targetDate.getDate() === today.getDate()
    );
  };

  const isPastDay = (day) => {
    const today = new Date();

    let targetMonth = date.getMonth();
    let targetYear = date.getFullYear();

    if (day < date.getDate()) {
      targetMonth += 1;
      if (targetMonth > 11) {
        targetMonth = 0;
        targetYear += 1;
      }
    }

    const targetDate = new Date(targetYear, targetMonth, day);
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    return targetDate < todayDate;
  };

  const closeModals = () => {
    setEnterRevenue(false);
    setOpenNewSchedule(false);
    setOpenDelete(false);
    setOpenCancelSchedule(false);
  };

  const handleRevenue = (item, chosenDay) => {
    if (item?.status === CalendarStatusType.CREATED) {
      apiService
        .updateRevenue({ id: item?._id, status: "SCHEDULED" })
        .then(() => {
          setIsUpdate(true);
        });
    } else {
      setDay(chosenDay);
      setChosenCalendar(item);
      setEnterRevenue(true);
    }
  };

  const handleAddSchedule = (day) => {
    setDateForNewSchedule(new Date(date?.getFullYear(), date?.getMonth(), day));
    setOpenNewSchedule(true);
  };

  const deleteSchedule = () => {
    apiService.deleteCalendar(chosenCalendar?._id).then(() => {
      setIsUpdate(true);
      setOpenDelete(false);
    });
  };

  const handleDeleteSchedule = (calendar) => {
    setChosenCalendar(calendar);
    setOpenDelete(true);
  };

  const handleCancelSchedule = (calendar) => {
    setChosenCalendarForCancel(calendar);
    setOpenCancelSchedule(true);
  };

  const handleSchedulePopup = (calendar, chosenDay) => {
    setSchedulePopupData({ calendar, day: chosenDay });
    setOpenSchedulePopup(true);
  };

  const normalizeUrlEntry = (entry) => {
    if (typeof entry === "string") {
      return { directUrl: entry, redirectUrl: entry };
    }
    return {
      directUrl: entry?.directUrl ?? entry?.direct_url ?? "",
      redirectUrl:
        entry?.redirectUrl ?? entry?.redirect_url ?? entry?.url ?? "",
    };
  };

  const handleCopyAndUse = async (
    calendar,
    { directUrl, redirectUrl, previewUrl }
  ) => {
    if (!calendar?._id) return;

    const existingUrls = (calendar.url ?? []).map(normalizeUrlEntry);
    const updatedUrls = [
      ...existingUrls,
      {
        directUrl: directUrl ?? "",
        redirectUrl: redirectUrl ?? "",
      },
    ];

    if (previewUrl) {
      try {
        await navigator.clipboard.writeText(previewUrl);
      } catch {
        // ignore clipboard errors
      }
    }

    await apiService.updateCalendarUrls(calendar._id, updatedUrls);

    const updatedCalendar = { ...calendar, url: updatedUrls };
    setSchedulePopupData((prev) =>
      prev?.calendar?._id === calendar._id
        ? { ...prev, calendar: updatedCalendar }
        : prev
    );
    setCreateLinkData((prev) =>
      prev?.calendar?._id === calendar._id
        ? { ...prev, calendar: updatedCalendar }
        : prev
    );
    setIsUpdate(true);
    setOpenCreateLink(false);
    setCreateLinkData(null);
  };

  const submitCancelSchedule = (payload) => {
    if (!chosenCalendarForCancel?._id || !payload) return;
    apiService
      .cancelCalendar(chosenCalendarForCancel._id, {
        cancel_reason_title: payload.cancel_reason_title ?? "",
        cancel_reason_description: payload.cancel_reason_description ?? "",
      })
      .then(() => {
        setIsUpdate(true);
        setOpenCancelSchedule(false);
      });
  };

  return (
    <div className={"Page"}>
      <SpecificCalendarHeader
        setOpenNewSchedule={setOpenNewSchedule}
        chosenList={chosenList}
        setChosenList={setChosenList}
        setIsUpdate={setIsUpdate}
      ></SpecificCalendarHeader>
      <div className={"px-6 w-full"}>
        <div className="TableWrapper">
          <div className="CalendarHeader">
            <button
              className={clsx("ChevronButtons", "ml-[16px]")}
              onClick={handlePreviousMonth}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/chevronLeft.svg"}
                className={"w-[18px] h-[18px]"}
              />
            </button>
            <button
              className={clsx("ChevronButtons", "ml-[6px]")}
              onClick={handleNextMonth}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/chevronRight.svg"}
                className={"w-[18px] h-[18px]"}
              />
            </button>
            <div className="text-base ml-3 font-semibold">
              {date.toLocaleString("default", {
                month: "short",
                year: "numeric",
              })}
            </div>
          </div>
          <div className="CalendarDaysLine">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
              (day, index) => (
                <div
                  key={day}
                  className={clsx(
                    "CalendarDayHeader",
                    isSameDay(day) && "border-b-[3px] border-b-[#7E50EA]"
                  )}
                >
                  <div
                    className={clsx(
                      "CalendarDayHeaderContent",
                      isSameDay(day) && "bg-[#EDE5FF]"
                    )}
                  >
                    <div
                      className={clsx(
                        "text-[#8185A8] text-center font-[Inter] text-[11px] font-bold leading-[18px]",
                        isSameDay(day) && "text-[#7E50EA]"
                      )}
                    >
                      {day.toUpperCase()}
                    </div>
                    <div
                      className={clsx(
                        "text-[#8185A8] text-center font-[Inter] text-[16px] font-semibold leading-[18px]",
                        isSameDay(day) && "text-[#7E50EA]"
                      )}
                    >
                      {weekDays[index]}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div className="text-[#8185A8] text-right font-[Inter] text-[9px] font-medium leading-normal">
                      T. Revenue
                    </div>
                    <div className="text-[#8185A8] text-right font-[Inter] text-[12px] font-bold leading-normal">
                      {getTotalRevenueForDay(weekDays[index]) === 0
                        ? "-"
                        : `$${getTotalRevenueForDay(weekDays[index])
                            .toFixed(2)
                            .replace(/\.00$/, "")}`}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
          <div className="grid grid-cols-7 h-full">
            {Array.from({ length: 7 }, (_, index) => {
              const day = index + 1;
              return (
                <div
                  key={day}
                  className={clsx(
                    `px-[6px] pt-[6px] h-[730px] overflow-y-scroll flex flex-col border-r border-b`,
                    index > 4 && "bg-[#FAFAFC]"
                  )}
                >
                  <div>{getDayContent(weekDays[index])}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {creativeTooltip &&
        (() => {
          const { rect } = creativeTooltip;
          const fitsRight =
            rect.left + rect.width + TOOLTIP_ARROW_GAP + TOOLTIP_WIDTH <=
            window.innerWidth;
          const openOnLeft = !fitsRight;
          return createPortal(
            <div
              className={`z-[9999] ${
                openOnLeft
                  ? "CreativeNameTooltipSideLeft"
                  : "CreativeNameTooltipSide"
              }`}
              style={{
                position: "fixed",
                left: openOnLeft
                  ? rect.left - TOOLTIP_WIDTH - TOOLTIP_ARROW_GAP
                  : rect.left + rect.width + TOOLTIP_ARROW_GAP,
                top: rect.top + rect.height / 2,
                transform: "translateY(-50%)",
              }}
              onMouseEnter={() =>
                clearTimeout(creativeTooltipCloseTimeoutRef.current)
              }
              onMouseLeave={() => {
                setCreativeTooltip(null);
                setCopiedCreativeField(null);
              }}
            >
              <div className="flex flex-col items-start gap-2 self-stretch">
                <div className="mb-1.5 flex items-center gap-1.5 font-semibold">
                  <img
                    alt=""
                    src="/media/icons/tooltip-eye.svg"
                    className="h-[14px] w-[14px]"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  Preview
                </div>
                <div className="h-[1px] self-stretch bg-white opacity-20" />
              </div>
              <div className="flex flex-col items-start gap-2 self-stretch pt-2 pb-3">
                <div className="flex items-start gap-[10px] self-stretch">
                  <div className="min-w-[60px] text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                    Name
                  </div>
                  <div className="text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                    :
                  </div>
                  <div className="flex items-start justify-between gap-2 flex-1">
                    <div className="text-white font-[Inter] text-[11px] font-medium leading-[18px] break-all">
                      {creativeTooltip.value?.creative_id?.name}
                    </div>
                    <button
                      type="button"
                      className={
                        copiedCreativeField === "name"
                          ? "StrategyIdTooltipCopiedButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                          : "StrategyIdTooltipCopyButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                      }
                      onClick={() =>
                        handleCopyCreativeField(
                          "name",
                          creativeTooltip.value?.creative_id?.name
                        )
                      }
                    >
                      {copiedCreativeField === "name" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
                <div className="h-[1px] self-stretch bg-white opacity-20" />
                <div className="flex items-start gap-[10px] self-stretch">
                  <div className="min-w-[60px] text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                    Message
                  </div>
                  <div className="text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                    :
                  </div>
                  <div className="flex items-start justify-between gap-2 flex-1">
                    <div className="text-white font-[Inter] text-[11px] font-medium leading-[18px] break-all">
                      {creativeTooltip.value?.creative_id?.note}
                    </div>
                    <button
                      type="button"
                      className={
                        copiedCreativeField === "message"
                          ? "StrategyIdTooltipCopiedButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                          : "StrategyIdTooltipCopyButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                      }
                      onClick={() =>
                        handleCopyCreativeField(
                          "message",
                          creativeTooltip.value?.creative_id?.note
                        )
                      }
                    >
                      {copiedCreativeField === "message" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}
      {idTooltipValue &&
        idTooltipAnchorRect &&
        createPortal(
          <div
            ref={idTooltipRef}
            className="StrategyIdTooltip StrategyIdTooltipSide z-[9999]"
            style={{
              position: "fixed",
              left: idTooltipAnchorRect.left - TOOLTIP_ARROW_GAP,
              top: idTooltipAnchorRect.top + idTooltipAnchorRect.height / 2,
              transform: "translate(-100%, -50%)",
            }}
          >
            <div className="StrategyIdTooltipHeader">
              <div className="flex items-center gap-1">
                <img
                  alt=""
                  src="/media/icons/code-02-white.svg"
                  className="h-[14px] w-[14px]"
                />
                <div className="text-white font-['Inter'] text-xs font-semibold leading-[18px]">
                  ID
                </div>
              </div>
              <div className="h-[1px] self-stretch bg-white opacity-20"></div>
            </div>
            <div className="flex flex-col items-start gap-2 py-2 self-stretch">
              <div className="flex items-center gap-[10px] self-stretch">
                <div className="text-white font-['Inter'] text-xs font-medium leading-[18px] flex-[1_0_0]">
                  {idTooltipValue?._id ?? "—"}
                </div>
                <button
                  type="button"
                  className={
                    idCopied
                      ? "StrategyIdTooltipCopiedButton"
                      : "StrategyIdTooltipCopyButton"
                  }
                  onClick={handleCopyId}
                >
                  {idCopied ? (
                    <>
                      <img
                        alt=""
                        src="/media/icons/check-circle-white.svg"
                        className="h-[14px] w-[14px]"
                      />
                      Copied
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_3753_45955)">
                          <path
                            d="M4.66797 4.6665V3.03317C4.66797 2.37978 4.66797 2.05308 4.79513 1.80352C4.90698 1.58399 5.08546 1.40552 5.30498 1.29366C5.55454 1.1665 5.88124 1.1665 6.53464 1.1665H10.968C11.6214 1.1665 11.9481 1.1665 12.1976 1.29366C12.4171 1.40552 12.5956 1.58399 12.7075 1.80352C12.8346 2.05308 12.8346 2.37978 12.8346 3.03317V7.4665C12.8346 8.1199 12.8346 8.4466 12.7075 8.69616C12.5956 8.91568 12.4171 9.09416 12.1976 9.20601C11.9481 9.33317 11.6214 9.33317 10.968 9.33317H9.33464M3.03464 12.8332H7.46797C8.12136 12.8332 8.44806 12.8332 8.69762 12.706C8.91715 12.5942 9.09562 12.4157 9.20748 12.1962C9.33464 11.9466 9.33464 11.6199 9.33464 10.9665V6.53317C9.33464 5.87978 9.33464 5.55308 9.20748 5.30352C9.09562 5.08399 8.91715 4.90552 8.69762 4.79366C8.44806 4.6665 8.12136 4.6665 7.46797 4.6665H3.03464C2.38124 4.6665 2.05454 4.6665 1.80498 4.79366C1.58546 4.90552 1.40698 5.08399 1.29513 5.30352C1.16797 5.55308 1.16797 5.87978 1.16797 6.53317V10.9665C1.16797 11.6199 1.16797 11.9466 1.29513 12.1962C1.40698 12.4157 1.58546 12.5942 1.80498 12.706C2.05454 12.8332 2.38124 12.8332 3.03464 12.8332Z"
                            stroke="white"
                            strokeWidth="1.33"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_3753_45955">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Copy ID
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {enterRevenue && (
        <Revenue
          close={closeModals}
          setIsUpdate={setIsUpdate}
          calendar={chosenCalendar}
          date={date}
          day={day}
        ></Revenue>
      )}
      {openNewSchedule && (
        <NewSchedule
          close={closeModals}
          setIsUpdate={setIsUpdate}
          openChooseDay={true}
          startDate={dateForNewSchedule}
          chosenLists={[chosenList]}
        ></NewSchedule>
      )}
      {openDelete && (
        <DeleteSchedule
          setOpenDelete={setOpenDelete}
          id={""}
          calendar={chosenCalendar}
          close={closeModals}
          deleteSchedule={deleteSchedule}
        />
      )}
      {openCancelSchedule && (
        <CancelSchedule
          item={chosenCalendarForCancel}
          calendars={chosenCalendarForCancel ? [chosenCalendarForCancel] : []}
          close={closeModals}
          deleteSchedule={submitCancelSchedule}
          submitWithReason={true}
        />
      )}
      {openSchedulePopup && (
        <SchedulePopup
          item={schedulePopupData?.calendar}
          day={schedulePopupData?.day}
          onCreateLink={(calendar, chosenDay) => {
            setCreateLinkData({ calendar, day: chosenDay });
            setOpenCreateLink(true);
          }}
          onSchedule={(calendar, chosenDay) => {
            handleRevenue(calendar, chosenDay);
          }}
          close={() => {
            setOpenSchedulePopup(false);
            setSchedulePopupData(null);
          }}
        />
      )}
      {openCreateLink && (
        <CreateLink
          item={createLinkData?.calendar}
          day={createLinkData?.day}
          onCopyAndUse={(linkData) =>
            handleCopyAndUse(createLinkData?.calendar, linkData)
          }
          close={() => {
            setOpenCreateLink(false);
            setCreateLinkData(null);
          }}
        />
      )}
    </div>
  );
};

export default SpecificCalendar;
